import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { PROMPT_VAR_SCOPE_LABEL } from '../lib/prompt-var-scope';
import type { ApiPromptVar } from '../lib/prompt-vars';
import { absolute } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// The account's prompt vars as a data table (spec §3.15): scope + ref, the
// `#{{key}}` it resolves, a one-line value preview, when it last changed, Edit +
// Delete. Presenter — the route owns the rows, the scope filter and every write.
//
// The value is rendered, not masked: `prompt_vars` is plaintext jsonb whose value
// is interpolated into prompt text and handed back verbatim by every read, so
// there is nothing here a mask would protect. What keeps that honest is the WRITE
// guard — a credential-shaped value is refused at both doors (422), so one can no
// longer be filed here at all.

const PREVIEW_MAX = 120;

/** One line of the value, elided — the full text lives in the edit form. */
export function previewValue(value: unknown): string {
  const text = JSON.stringify(value) ?? String(value);
  const flat = text.replace(/\s+/g, ' ');
  return flat.length > PREVIEW_MAX ? `${flat.slice(0, PREVIEW_MAX)}…` : flat;
}

export function PromptVarTable(props: {
  readonly rows: readonly ApiPromptVar[];
  readonly onEdit: (promptVar: ApiPromptVar) => void;
  readonly onDelete: (promptVar: ApiPromptVar) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<ApiPromptVar>[] = [
    {
      key: 'scope',
      header: t('account_prompt_vars.col_scope'),
      cell: (v) => <span class="badge badge--outline">{t(PROMPT_VAR_SCOPE_LABEL[v.scope])}</span>,
    },
    {
      key: 'ref',
      header: t('account_prompt_vars.col_ref'),
      mono: true,
      cell: (v) => v.ref ?? UNSET,
    },
    {
      key: 'key',
      rowHeader: true,
      header: t('account_prompt_vars.col_key'),
      mono: true,
      cell: (v) => v.key,
    },
    {
      key: 'value',
      header: t('account_prompt_vars.col_value'),
      mono: true,
      cell: (v) => previewValue(v.value),
    },
    {
      key: 'updated',
      header: t('account_prompt_vars.col_updated'),
      cell: (v) => absolute(v.updated_iso),
    },
    {
      key: 'actions',
      header: t('account_prompt_vars.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      cell: (v) => (
        <div class="data-table__actions">
          <Button variant="ghost" size="sm" onClick={() => props.onEdit(v)}>
            {t('account_prompt_vars.edit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => props.onDelete(v)}>
            {t('account_prompt_vars.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      label={t('account_prompt_vars.table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(v) => v.id}
    />
  );
}
